import { useState } from 'react'
import { InteractiveRenderer } from '@aurora/interactives'
import { pathStepHtml, type InteractiveResult } from '@aurora/core'
import { useAuroraStore } from '../../store/auroraStore'
import { skillLabels } from '../../store/selectors'
import { difficultyLabel, hasHtml, kindLabel, readSpec } from '../../lib/specs'
import { useResolvedTheme } from '../../hooks/useTheme'
import { MaterialFrame } from '../kid/MaterialFrame'

/**
 * A game played in place, by the parent, on its own page. The page above it
 * already names the game, so the preview is only the stage.
 *
 * The preview is the real thing, playable — a still picture of an activity
 * tells a parent nothing about whether it works, and the one question worth
 * answering here is "would this hold her for ten minutes?". Playing it through
 * once is the only way to know.
 *
 * It goes through the same `readSpec` guard Kid Mode uses, so a malformed spec
 * is caught here, in front of the adult, rather than in front of the child.
 */
export function SpecPreview({ artifact }: { artifact: any }) {
  const labels = useAuroraStore(skillLabels)
  const theme = useResolvedTheme()
  const [attempt, setAttempt] = useState(0)
  const [played, setPlayed] = useState<InteractiveResult | null>(null)

  const read = readSpec(artifact)

  // A generated document: play it here, in the same sandbox Kid Mode uses.
  if (hasHtml(artifact)) {
    return (
      <div className="preview">
        <div className="preview-stage">
          <MaterialFrame
            key={attempt}
            html={pathStepHtml(artifact) ?? artifact.html}
            theme={theme}
            assets={artifact.assets}
            onResult={(r) => setPlayed(r)}
          />
        </div>
        {played && (
          <p className="preview-played">
            {played.completed ? 'Played through' : 'Stopped partway'}{played.score ? ` · ${played.score.correct} of ${played.score.total}` : ''}.
            {' '}
            <button className="btn-link" onClick={() => { setPlayed(null); setAttempt((n) => n + 1) }}>Play again</button>
          </p>
        )}
      </div>
    )
  }

  if (!read.ok) {
    return (
      <div className="preview">
        <div className="preview-broken">
          <p className="preview-broken-title">This one came back malformed</p>
          <p className="preview-broken-body">
            Nothing was saved and nothing reached Kid Mode. Asking again, or describing it a
            little differently, usually gets a clean one.
          </p>
          <ul className="preview-broken-list">
            {read.errors.slice(0, 4).map((e) => (
              <li key={e}>{e}</li>
            ))}
          </ul>
        </div>
      </div>
    )
  }

  const spec = read.spec
  const skills = spec.targetSkillIds ?? []

  return (
    <div className="preview">
      <p className="preview-meta">
        {kindLabel(spec.kind)} · {difficultyLabel(spec.difficulty)}
        {spec.theme ? ` · ${spec.theme}` : ''}
      </p>

      {skills.length > 0 && (
        <ul className="preview-skills">
          {skills.map((id) => (
            <li key={id} className="preview-skill">
              {labels[id] ?? id}
            </li>
          ))}
        </ul>
      )}

      <div className="preview-stage">
        <InteractiveRenderer
          key={attempt}
          spec={spec}
          theme={theme}
          onResult={(result: InteractiveResult) => setPlayed(result)}
        />
      </div>

      <footer className="preview-foot">
        <p className="preview-hint">
          {played
            ? played.completed
              ? 'Plays through cleanly.'
              : 'Ended early. Worth another look before you keep it.'
            : 'Try it yourself. This is exactly what she will see.'}
        </p>
        {played && (
          <button className="btn btn-quiet btn-sm" onClick={() => {
            setPlayed(null)
            setAttempt((n) => n + 1)
          }}>
            Reset preview
          </button>
        )}
      </footer>
    </div>
  )
}
